import { fork } from '@cortex/utils/fork';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Table, type IColumn } from '../../components/common/Table';
import { useCreateDocumentSet, useDocumentSets } from '../../hooks/useDocumentSets';
import { routes } from '../../lib/constants';
import { formatRelative } from '../../lib/format';
import type { IDocumentSet } from '../../lib/metaphysics';

export const Route = createFileRoute('/document-sets/')({ component: DocumentSetsPage });

function DocumentSetsPage() {
  const list = useDocumentSets();
  const create = useCreateDocumentSet();
  const nav = useNavigate();
  const [openCreate, setOpenCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  function reset() {
    setName('');
    setDescription('');
    create.reset();
  }

  async function submit() {
    if (!name.trim()) return;
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      reset();
      setOpenCreate(false);
      await nav({ to: routes.documentSetDetail(created.id) });
    } catch (err) {
      console.error('document-sets/create', err);
    }
  }

  const cols: IColumn<IDocumentSet>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (s) => (
        <Link
          to={routes.documentSetDetail(s.id)}
          style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}
        >
          {s.name}
        </Link>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      cell: (s) => (
        <span className="t-secondary">{s.description ?? <span className="t-muted">—</span>}</span>
      ),
    },
    {
      key: 'updated',
      header: 'Updated',
      mono: true,
      cell: (s) => formatRelative(s.updatedAt),
      width: 140,
    },
  ];

  return (
    <>
      <PageHeader
        title="Document sets"
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setOpenCreate(true)}
          >
            New set
          </button>
        }
      />
      <Table<IDocumentSet>
        rows={list.data}
        columns={cols}
        rowKey={(s) => s.id}
        isLoading={list.isLoading}
        error={list.error as Error | null}
        emptyTitle="No document sets yet"
        emptyDescription="Create a set to start organising knowledge files into versioned bundles."
        emptyCta={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setOpenCreate(true)}
          >
            New set
          </button>
        }
        onRowClick={async (s) => {
          try {
            await nav({ to: routes.documentSetDetail(s.id) });
          } catch (err) {
            console.error(err);
          }
        }}
      />

      <Modal
        open={openCreate}
        onClose={() => {
          setOpenCreate(false);
          reset();
        }}
        title="New document set"
        eyebrow="DOCUMENT SETS · NEW"
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setOpenCreate(false);
                reset();
              }}
              disabled={create.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => {
                fork(submit());
              }}
              disabled={create.isPending || !name.trim()}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={true}
              required={true}
            />
          </label>
          <label className="field">
            <span className="field-label">Description (optional)</span>
            <textarea
              className="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          {create.error ? (
            <div className="alert alert--danger">
              Failed to create: {(create.error as Error).message}
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
